import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Send, Loader2, FileText, Image as ImageIcon, BadgeCheck, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Kinetic";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PRESS_ITEMS = [
  { icon: ImageIcon, title: "LABEL LOGO", desc: "Official gothic logo, transparent-ready PNG", href: `${BACKEND}/api/presskit/logo`, testid: "presskit-logo" },
  { icon: ImageIcon, title: "ARTIST PHOTO", desc: "Mike Millzz GET-LOW press photo, hi-res PNG", href: `${BACKEND}/api/presskit/photo`, testid: "presskit-photo" },
  { icon: FileText, title: "LABEL BIO", desc: "One-page PDF bio for venues & media", href: `${BACKEND}/api/presskit/bio.pdf`, testid: "presskit-bio" },
];

export default function LabelExtras() {
  const [artists, setArtists] = useState([]);
  const [releases, setReleases] = useState([]);
  const [form, setForm] = useState({ artist_name: "", email: "", link: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [booking, setBooking] = useState({ name: "", email: "", city: "", date: "", event_type: "SHOW", message: "" });
  const [bookingSending, setBookingSending] = useState(false);
  const [bookingSent, setBookingSent] = useState(false);

  useEffect(() => {
    api.get("/artists").then((r) => setArtists(r.data)).catch(() => {});
    api.get("/tracks").then((r) => {
      const map = {};
      r.data.forEach((t) => {
        if (!map[t.album]) map[t.album] = { album: t.album, cover: t.cover, count: 0 };
        map[t.album].count += 1;
      });
      setReleases(Object.values(map));
    }).catch(() => {});
  }, []);

  const submitDemo = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/demos", form);
      setSent(true);
      toast.success("Demo received — the label will be in touch.");
    } catch {
      toast.error("Couldn't send — try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <section data-testid="label-roster" className="mx-auto mt-32 max-w-7xl px-6">
        <Reveal>
          <p className="mb-3 font-mono text-xs tracking-[0.4em] text-primary">05 / THE ROSTER</p>
          <h2 className="font-anton text-5xl text-white md:text-7xl">THE ARTISTS</h2>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {artists.map((a, i) => {
            const Card = (
              <div
                data-testid={`label-artist-${a.id}`}
                className="group flex h-full flex-col items-center gap-8 border border-white/10 bg-card p-8 transition-colors duration-300 hover:border-primary/50 sm:flex-row md:p-10"
              >
                {a.photo ? (
                  <img
                    src={a.photo}
                    alt={a.name}
                    className="h-40 w-40 shrink-0 border border-primary/30 object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                ) : (
                  <div className="flex h-40 w-40 shrink-0 items-center justify-center border border-white/15 bg-black font-anton text-5xl text-primary">
                    {a.name.charAt(0)}
                  </div>
                )}
                <div className="text-center sm:text-left">
                  <p className="font-mono text-xs tracking-[0.4em] text-primary">{a.handle || a.role}</p>
                  <h3 className="mt-2 font-anton text-4xl text-white">{a.name}</h3>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-white/40">{a.role}</p>
                  {a.bio && <p className="mt-3 text-sm leading-relaxed text-white/60">{a.bio}</p>}
                  {a.link && (
                    <span className="mt-4 inline-flex items-center gap-2 font-mono text-xs tracking-[0.3em] text-white/60 transition-colors duration-300 group-hover:text-primary">
                      ARTIST PROFILE <ArrowRight size={14} />
                    </span>
                  )}
                </div>
              </div>
            );
            return (
              <Reveal key={a.id} delay={i * 0.08}>
                <Link to={`/artist/${a.id}`} className="block h-full">{Card}</Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section data-testid="label-releases" className="mx-auto mt-32 max-w-7xl px-6">
        <Reveal>
          <p className="mb-3 font-mono text-xs tracking-[0.4em] text-primary">06 / THE CATALOG</p>
          <h2 className="font-anton text-5xl text-white md:text-7xl">LABEL RELEASES</h2>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {releases.map((r, i) => (
            <Reveal key={r.album} delay={i * 0.08}>
              <Link
                to="/music"
                data-testid={`label-release-${r.album.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group block border border-white/10 bg-card"
              >
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={r.cover}
                    alt={r.album}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-primary/0 transition-colors duration-500 group-hover:bg-primary/10" />
                  <span className="absolute bottom-4 left-4 bg-black/70 px-3 py-1 font-mono text-[10px] tracking-[0.25em] text-primary backdrop-blur">
                    {r.count} {r.count === 1 ? "TRACK" : "TRACKS"}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-white/10 p-5">
                  <span className="font-oswald text-lg font-bold tracking-wider text-white">{r.album}</span>
                  <ArrowRight size={16} className="text-white/40 transition-all duration-300 group-hover:translate-x-1 group-hover:text-primary" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="label-demo" className="mx-auto mt-32 max-w-7xl px-6">
        <div className="grid gap-16 lg:grid-cols-2">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.4em] text-primary">07 / NEW TALENT</p>
            <h2 className="mt-4 font-anton text-5xl leading-tight text-white md:text-6xl">
              SEND YOUR <span className="text-primary glow-blue">DEMO</span>
            </h2>
            <p className="mt-6 max-w-md leading-relaxed text-white/65">
              Southern Smoke is always listening. If your sound is rooted in the South and your pen is honest,
              drop a link — every submission gets heard by the label.
            </p>
            <ul className="mt-8 space-y-3 font-mono text-xs tracking-[0.15em] text-white/50">
              <li className="flex items-center gap-3"><BadgeCheck size={14} className="text-primary" /> STREAMING LINK REQUIRED (SOUNDCLOUD, YOUTUBE, ETC.)</li>
              <li className="flex items-center gap-3"><BadgeCheck size={14} className="text-primary" /> UNRELEASED OR RELEASED — BOTH WELCOME</li>
              <li className="flex items-center gap-3"><BadgeCheck size={14} className="text-primary" /> RESPONSE WITHIN 14 DAYS IF IT'S A FIT</li>
            </ul>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="border border-white/10 bg-card p-10">
              {sent ? (
                <div data-testid="demo-success" className="flex items-center gap-4 border border-primary/40 bg-primary/10 p-6">
                  <span className="bg-primary p-2 text-black"><Check size={18} /></span>
                  <p className="font-mono text-xs tracking-[0.2em] text-primary">DEMO RECEIVED. STAY CLOSE TO YOUR EMAIL.</p>
                </div>
              ) : (
                <form data-testid="demo-form" onSubmit={submitDemo} className="space-y-7">
                  <div>
                    <label htmlFor="demo-name" className="font-mono text-[10px] tracking-[0.3em] text-white/50">ARTIST NAME</label>
                    <input id="demo-name" data-testid="demo-name-input" required value={form.artist_name}
                      onChange={(e) => setForm({ ...form, artist_name: e.target.value })}
                      placeholder="Your stage name"
                      className="mt-2 w-full border-b border-white/20 bg-transparent py-3 text-white outline-none transition-colors duration-300 placeholder:text-white/25 focus:border-primary" />
                  </div>
                  <div>
                    <label htmlFor="demo-email" className="font-mono text-[10px] tracking-[0.3em] text-white/50">EMAIL</label>
                    <input id="demo-email" data-testid="demo-email-input" type="email" required value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@email.com"
                      className="mt-2 w-full border-b border-white/20 bg-transparent py-3 text-white outline-none transition-colors duration-300 placeholder:text-white/25 focus:border-primary" />
                  </div>
                  <div>
                    <label htmlFor="demo-link" className="font-mono text-[10px] tracking-[0.3em] text-white/50">DEMO LINK</label>
                    <input id="demo-link" data-testid="demo-link-input" type="url" required value={form.link}
                      onChange={(e) => setForm({ ...form, link: e.target.value })}
                      placeholder="https://soundcloud.com/..."
                      className="mt-2 w-full border-b border-white/20 bg-transparent py-3 text-white outline-none transition-colors duration-300 placeholder:text-white/25 focus:border-primary" />
                  </div>
                  <div>
                    <label htmlFor="demo-message" className="font-mono text-[10px] tracking-[0.3em] text-white/50">MESSAGE (OPTIONAL)</label>
                    <textarea id="demo-message" data-testid="demo-message-input" rows={3} value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Tell us about your sound..."
                      className="mt-2 w-full resize-none border-b border-white/20 bg-transparent py-3 text-white outline-none transition-colors duration-300 placeholder:text-white/25 focus:border-primary" />
                  </div>
                  <button type="submit" data-testid="demo-submit-button" disabled={sending}
                    className="flex items-center gap-3 bg-primary px-8 py-4 font-mono text-sm font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#fff] disabled:opacity-50">
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    {sending ? "SENDING..." : "SUBMIT DEMO"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="label-presskit" className="mx-auto mt-32 max-w-7xl px-6">
        <div className="grid gap-16 lg:grid-cols-2">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.4em] text-primary">08 / FOR VENUES & MEDIA</p>
            <h2 className="mt-4 font-anton text-5xl text-white md:text-6xl">PRESS KIT</h2>
            <div className="mt-10 space-y-4">
              {PRESS_ITEMS.map((p) => (
                <a
                  key={p.title}
                  href={p.href}
                  download
                  data-testid={`${p.testid}-download`}
                  className="group flex items-center gap-6 border border-white/10 bg-card p-6 transition-colors duration-300 hover:border-primary/50"
                >
                  <p.icon size={26} className="shrink-0 text-primary transition-transform duration-300 group-hover:-translate-y-1" />
                  <div className="flex-1">
                    <h3 className="font-oswald text-lg font-bold tracking-wider text-white">{p.title}</h3>
                    <p className="mt-1 text-sm text-white/60">{p.desc}</p>
                  </div>
                  <span className="flex items-center gap-2 font-mono text-xs font-bold tracking-[0.25em] text-primary">
                    <Download size={14} /> GET
                  </span>
                </a>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <p className="font-mono text-xs tracking-[0.4em] text-primary">09 / BOOK THE LABEL</p>
            <h2 className="mt-4 font-anton text-5xl text-white md:text-6xl">
              BOOK <span className="text-primary glow-blue">US</span>
            </h2>
            <div className="mt-10 border border-white/10 bg-card p-8">
              {bookingSent ? (
                <div data-testid="booking-success" className="flex items-center gap-4 border border-primary/40 bg-primary/10 p-6">
                  <span className="bg-primary p-2 text-black"><Check size={18} /></span>
                  <p className="font-mono text-xs tracking-[0.2em] text-primary">REQUEST RECEIVED. BOOKING WILL RESPOND SOON.</p>
                </div>
              ) : (
                <form data-testid="booking-form" className="space-y-6"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setBookingSending(true);
                    try {
                      await api.post("/bookings", booking);
                      setBookingSent(true);
                      toast.success("Booking request sent.");
                    } catch {
                      toast.error("Couldn't send — try again.");
                    } finally {
                      setBookingSending(false);
                    }
                  }}>
                  <input data-testid="booking-name-input" required placeholder="Venue / promoter name" value={booking.name}
                    onChange={(e) => setBooking({ ...booking, name: e.target.value })}
                    className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
                  <input data-testid="booking-email-input" type="email" required placeholder="you@venue.com" value={booking.email}
                    onChange={(e) => setBooking({ ...booking, email: e.target.value })}
                    className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
                  <div className="grid gap-6 sm:grid-cols-2">
                    <input data-testid="booking-city-input" required placeholder="City, ST" value={booking.city}
                      onChange={(e) => setBooking({ ...booking, city: e.target.value })}
                      className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
                    <input data-testid="booking-date-input" type="date" required value={booking.date}
                      onChange={(e) => setBooking({ ...booking, date: e.target.value })}
                      className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none [color-scheme:dark] focus:border-primary" />
                  </div>
                  <select data-testid="booking-type-select" value={booking.event_type}
                    onChange={(e) => setBooking({ ...booking, event_type: e.target.value })}
                    className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none focus:border-primary">
                    {["SHOW", "FESTIVAL", "SHOWCASE", "PRIVATE EVENT", "FEATURE / VERSE"].map((t) => (
                      <option key={t} value={t} className="bg-black">{t}</option>
                    ))}
                  </select>
                  <textarea data-testid="booking-message-input" rows={3} placeholder="Capacity, budget range, details..." value={booking.message}
                    onChange={(e) => setBooking({ ...booking, message: e.target.value })}
                    className="w-full resize-none border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
                  <button type="submit" data-testid="booking-submit-button" disabled={bookingSending}
                    className="flex items-center gap-3 bg-primary px-8 py-4 font-mono text-sm font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#fff] disabled:opacity-50">
                    {bookingSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    {bookingSending ? "SENDING..." : "REQUEST BOOKING"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="label-join-cta" className="mx-auto mt-32 max-w-7xl px-6">
        <Reveal className="relative overflow-hidden border border-primary/30 bg-black p-10 text-center md:p-20">
          <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-primary/20 blur-[100px]" />
          <p className="mb-4 font-mono text-xs tracking-[0.4em] text-primary">ARTISTS — PRODUCERS — FANS</p>
          <h2 className="font-anton text-5xl leading-none text-white md:text-7xl">
            THE SMOKE IS <span className="text-primary glow-blue">RISING</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base text-white/60 md:text-lg">
            Join the list for label news, releases and opportunities — or go all in with the MILLZZ Circle.
          </p>
          <Link
            to="/subscribe"
            data-testid="label-join-button"
            className="mt-10 inline-flex items-center gap-3 bg-primary px-8 py-4 font-mono text-sm font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#fff]"
          >
            JOIN THE MOVEMENT <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>
    </>
  );
}
